import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Card, CardBody } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";

interface RepStaffProfile {
  id: number;
  display_name: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  agency_id: number | null;
  agency_name: string | null;
}

export function RepStaffProfilePage() {
  const params = useParams();
  const staffId = Number.parseInt(params.staffId ?? "", 10);
  const profileQuery = useQuery<{ row: RepStaffProfile }, ApiError>({
    queryKey: ["inside-sales", "rep-staff", staffId],
    queryFn: () => apiFetch<{ row: RepStaffProfile }>(`/api/inside-sales/rep-staff/${staffId}`),
    enabled: Number.isFinite(staffId) && staffId > 0,
  });
  const row = profileQuery.data?.row;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp
        hoverHint="Rep staff profile from sales order assignment and the rep staff directory."
        breadcrumbLabel={row?.display_name ?? "Staff"}
      >
        {row?.display_name ?? "Rep staff"}
      </PageTitleWithHelp>
      <Card>
        <CardBody className="space-y-3">
          {profileQuery.isLoading ? <p className="text-sm text-slate-600">Loading...</p> : null}
          {profileQuery.isError ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {profileQuery.error.message}
            </p>
          ) : null}
          {row != null ? (
            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</dt>
                <dd className="mt-1 text-sm text-slate-800">{row.email ?? " - "}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rep agency</dt>
                <dd className="mt-1 text-sm text-slate-800">
                  {row.agency_id != null && row.agency_name != null ? (
                    <Link className="text-sky-800 hover:underline" to={`/inside-sales/rep-agencies?agencyId=${row.agency_id}`}>
                      {row.agency_name}
                    </Link>
                  ) : (
                    " - "
                  )}
                </dd>
              </div>
            </dl>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
